import {
  ScrollContext,
  type ScrollContextType,
  THEME_LIGHT,
  ThemeContext,
  useEventListener,
  useThemeStorage,
} from "@hoylu/client-common";
import Scrollbar from "@hoylu/client-common/dist/assets/css.modules/scrollbar.module.css";
import { NotificationsContextProvider } from "@hoylu/notifications";
import { UserSettingsContextProvider } from "@hoylu/user-settings";
import React, { type CSSProperties, useEffect, useMemo, useState } from "react";
import type { Root } from "react-dom/client";
import { connect, Provider } from "react-redux";
import { Tooltip } from "react-tooltip";
import type { Store } from "redux";
import type { RootState } from "typesafe-actions";
import { APP_NODE_ID } from "../app.defaults";
import {
  authService,
  notificationsService,
} from "../state/config/config.selector";
import { AppMode } from "../state/mode/mode.reducer";
import { login } from "../state/user/user.actions";
import { userId, userToken } from "../state/user/user.selector";
import { getSelectedProjectColor } from "../state/workspaces/projects.selector";
import { isWorkspaceFullscreen } from "../state/workspaces/workspaces.selector";
import { Localized } from "../strings";
import { hexToRgba } from "../utils/hexToRgba";
import AccountVerificationError from "./AccountVerificationError";
import Styles from "./App.module.css";
import BetaDisabledContent from "./BetaDisabledContent";
import SimplifiedHeader from "./Header/SimplifiedHeader";
import Modals from "./modals";
import LoginPrompt from "./prompts/LoginPrompt";
import WorkspaceContent from "./WorkspaceContent";
import { WorkspaceLoading } from "./WorkspaceLoading";

const App: React.FC<AppProps> = ({
  isLoggedIn,
  showWorkspaceLoadingOverlay,
  isWorkspaceFullscreen,
  projectThemeColor,
  appMode,
  notificationServiceUrl,
  authServiceUrl,
  token,
  userId,
}) => {
  const themeContext = useThemeStorage(THEME_LIGHT);
  const strings = Localized.object("APP");
  // We aim to dynamically adjust the app's content background gradient according to the chosen project color.
  const gradient = useMemo(
    () =>
      projectThemeColor
        ? {
            backgroundImage: `linear-gradient(to bottom,
          ${hexToRgba(projectThemeColor)} 0%,
          var(--gradient-from-color-theme) 50%,
          var(--gradient-to-color-theme) 100%
        )`,
          }
        : {
            backgroundImage:
              "linear-gradient(to bottom, var(--theme-gradient))",
          },
    [projectThemeColor],
  ) as CSSProperties;

  useEffect(() => {
    const html = document.getElementsByTagName("html")[0];
    if (isWorkspaceFullscreen) {
      html.className = Styles.htmlHidden;
    } else {
      html.className = Styles.htmlAuto;
    }
  }, [isWorkspaceFullscreen]);

  const appRootClassName = useMemo(() => {
    const className = [Styles.app, themeContext.theme, Scrollbar.default];
    if (isWorkspaceFullscreen || isLoggedIn === "UNKNOWN") {
      className.push(Styles.appHidden);
    }
    return className.join(" ");
  }, [isWorkspaceFullscreen, isLoggedIn, themeContext.theme]);

  const tooltipStyles: CSSProperties = {
    color: "black",
    textAlign: "center",
    zIndex: "var(--tooltip-position)",
  };

  const self = useEventListener("scroll", (e) => {
    const tar = e.currentTarget as HTMLDivElement;
    setScrollData({ scrollX: tar.scrollLeft, scrollY: tar.scrollTop });
  });

  const [scrollData, setScrollData] = useState<ScrollContextType>({
    scrollX: undefined,
    scrollY: undefined,
  });

  if (appMode === AppMode.BETA_NOT_ALLOWED) return <BetaDisabledContent />;

  if (
    [
      AppMode.NOT_VERIFIED_POLICY_RESTRICTED,
      AppMode.SKIPPABLE_VERIFIED_POLICY_RESTRICTED,
    ].includes(appMode)
  )
    return <AccountVerificationError />;

  return (
    <ThemeContext.Provider value={themeContext}>
      <NotificationsContextProvider
        serverUrl={notificationServiceUrl}
        token={token}
      >
        <UserSettingsContextProvider
          serverUrl={authServiceUrl}
          token={token}
          userId={userId}
        >
          <div
            className={appRootClassName}
            data-test-id={APP_NODE_ID}
            id={APP_NODE_ID}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => e.preventDefault()}
            ref={self}
            style={gradient}
          >
            <Modals />
            {isLoggedIn === "UNKNOWN" && (
              <>
                <SimplifiedHeader />
                <div className={Styles.loadingWorkspace}>
                  {strings.LOADING_YOUR_WORKSPACE}
                </div>
              </>
            )}
            <ScrollContext.Provider value={scrollData}>
              {isLoggedIn !== "UNKNOWN" &&
                (isLoggedIn ? <WorkspaceContent /> : <LoginPrompt />)}
              {showWorkspaceLoadingOverlay && <WorkspaceLoading />}
              <Tooltip id="tooltip" style={tooltipStyles} />
            </ScrollContext.Provider>
          </div>
        </UserSettingsContextProvider>
      </NotificationsContextProvider>
    </ThemeContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  appMode: state.mode,
  isLoggedIn: state.context.user.isLoggedIn,
  isWorkspaceFullscreen: isWorkspaceFullscreen(state),
  projectThemeColor: getSelectedProjectColor(state),
  showWorkspaceLoadingOverlay:
    (!!state.context.workspaces.waitingToEditID ||
      state.context.workspaces.isCreatingNewWorkspaceUrl) &&
    (state.mode === AppMode.LOADING_DASHBOARD ||
      state.mode === AppMode.DASHBOARD),
  notificationServiceUrl: notificationsService(state),
  authServiceUrl: authService(state),
  token: userToken(state),
  userId: userId(state),
});

const mapDispatchToProps = {
  login: login.request,
};

type AppProps = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const ConnectedApp = connect(mapStateToProps, mapDispatchToProps)(App);

export function renderApp(rootElement: Root, reduxStore: Store) {
  rootElement.render(
    <Provider store={reduxStore}>
      <ConnectedApp />
    </Provider>,
  );
}

export default renderApp;
